import { gql } from "../__generated__";

export const CREATE_WORKER = gql(`
    mutation CreateWorkerProfile($workerProfile: WorkerProfileInput!) {
        createWorkerProfile(workerProfile: $workerProfile) {
            id
        }
    }
`);

export const CREATE_CLIENT_PROFILE = gql(`
    mutation CreateClientProfile($clientProfile: ClientProfileInput!) {
        createClientProfile(clientProfile: $clientProfile) {
            id
        }
    }
`);

export const CREATE_PRIVATE_CLIENT_PROFILE = gql(`
    mutation CreatePrivateClientProfile($privateClientProfile: PrivateClientProfileInput!) {
        createPrivateClientProfile(privateClientProfile: $privateClientProfile) {
            id
        }
    }
`);

gql(`
    fragment MissionInfoFields on MissionInfo {
        clientInfo {
            ...ClientInfoFields
        }
        signerInfo {
            ...ClientInfoFields
        }
        name
        earningType
        compensateOvertime
        payrollSource
        activityType {
            title
            ofsSalariumId
            ofsSalariumGroupId
            id
        }
        workerInfo {
            ...WorkerInfoFields
        }
        id
        workingTimeClassicSpec {
            repetition
            unit
            unitCount
            dailyHours
        }
    }
`);

gql(`
    fragment DayScheduleFields on DayScheduleType {
        freeDurations {
            type
            duration
        }
        timeMode
        fixedTimes {
            endTime
            startTime
            type
        }
    }
`);

gql(`
    fragment MissionFeeFields on MissionFee {
        id
        type
        amount
        invoiceAmount
        repetition
        otherTitle
        externalId
        isAvsSalaryExempt
        countUnit
    }
`);

gql(`
    fragment MissionDeductionFields on MissionDeduction {
        id
        amount
        repetition
        description
        externalId
    }
`);

gql(`
    fragment WorkerGroupFields on WorkerGroup {
        id
        name
        description
    }
`);

gql(`
    fragment CoreContractFields on Contract {
        id
        state
        signingState
        signatureStatus
        startDate
        effectiveEndDate
        endDate
        workerAcceptedAt
        clientAcceptedAt
        workerContractSignedAt
        workerContractSealedAt
        clientContractSignedAt
        clientContractSealedAt
        cctEditionId
        cctContractId
        jobTitle
        jobDescription
        cctDetails {
            ...CCTDetailsFieldFullValueFields
        }
        earningType
        workingPercentage
        workingAddress {
            ...AddressFields
        }
        useClientWorkingAddress
        baseSalary
        grossSalary
        netSalary
        deferredIndemnity
        invoiceSalary
        has13thSalary
        vacationDays
        publicHolidaysCompensation
        publicHolidaysCompensationRate
        thirteenMonthCompensation
        thirteenMonthCompensationRate
        vacationCompensation
        vacationCompensationRate
        lseMargin
        clientDeductions {
                title
                quantity
                rate
                amount
                intercomArticleId
                i18nValue
                appliedMonthly
        }
        workerDeductions {
                title
                quantity
                rate
                amount
                intercomArticleId
                i18nValue
                appliedMonthly
        }
        hoursPerWeekFullTime
        terminatedBy
        terminatedDate
        terminationReason
        missionInfo {
            ...MissionInfoFields
        }
        terminationOwner {
            id
            avatarUrl
            firstName
            lastName
        }
        terminationOtherReason
        createdAt
        updatedAt
        contractEffectiveAsset {
            ...AssetFields
        }
        workingTimeClassicSpec {
            repetition
            unit
            unitCount
            dailyHours
        }
        fees {
            ...MissionFeeFields
        }
        deductions {
            ...MissionDeductionFields
        }
        lppPlan {
          ...LPPPlanFields
        }
        ijmInfo {
            ijmGroup {
                ...IjmGroupField
            }
            payFirstDays
            payRemainingPercents
        }
        disableAvsSmallSalaryExemption
        forceLpp
        payVacationIndemnityWithSalary
        pay13thSalaryWithSalary
    }
`);

gql(`
    fragment CoreMissionFields on Mission {
        id
        name
        educationLevel
        createdAt
        updatedAt
        laaGroupId
        payrollSource
        amendmentSigningState
        startDate
        endDate
        effectiveEndDate
        legalEmployer {
            id
            name
            marketingName
            useLseCct
        }
        activityType {
            id
            title
            ofsSalariumId
            ofsSalariumGroupId
        }
        clientInfo {
            ...ClientInfoFields
        }
        signerInfo {
            ...ClientInfoFields
        }        
        workerInfo {
            ...WorkerInfoFields
        }
        currentContract {
            ...CoreContractFields
        }
        # Set only once an applied amendment has replaced the contract the mission was signed with,
        # in which case currentContract serves the amendment document and this is the signed PDF.
        initialContractPdf
        documents {
            id
            type
            title
            documentUrl
            inlineContent
        }
        terminationInfo {
            newState
            minimalEndDate
            cancellationRequest {
                ...MissionCancellationRequestFields
            }
        }
        workerGroup {
            ...WorkerGroupFields
        }        
    }
`);

gql(`
    fragment MissionCancellationRequestFields on MissionCancellationRequest {
        id
        createdAt
        reason
        otherReason
        isCancelledBySelf
        requestedRole
        terminationDate
    }
`);

export const GET_MISSION = gql(`
    query getMission($id: ID!) {
        getMission(id: $id) {
            ...CoreMissionFields
        }
    }
`);

export const GET_MISSION_WITH_TERMINATION = gql(`
    query getMissionWithTermination($id: ID!) {
        getMission(id: $id) {
            ...CoreMissionFields
        }
        getTerminationInfo(missionId: $id) {
            newState
            minimalEndDate
            cancellationRequest {
                ...MissionCancellationRequestFields
            }
        }
    }
`);

export const CREATE_MISSION = gql(`
    mutation CreateMission($missionData: MissionInput!, $generateContractAsset: Boolean) {
        createMission(
            missionData: $missionData
            generateContractAsset: $generateContractAsset
        ) {
            ...CoreMissionFields
        }
    }
`);

export const UPDATE_MISSION = gql(`
    mutation UpdateMission(
        $id: ID!
        $missionData: MissionInput!
        $generateContractAsset: Boolean
    ) {
        updateMission(
            id: $id
            missionData: $missionData
            generateContractAsset: $generateContractAsset
        ) {
            ...CoreMissionFields
        }
    }
`);

export const UPDATE_MISSION_DOCUMENTS = gql(`
    mutation UpdateMissionDocuments(
        $missionId: ID!
        $documents: [DocumentInput!]!
    ) {
        updateMissionDocuments(
            missionId: $missionId
            documents: $documents
        ) {
            ...CoreMissionFields
        }
    }
`);

export const CHANGE_MISSION_OWNER = gql(`
    mutation ChangeMissionOwner(
        $missionId: ID!
        $clientUserId: ID!
    ) {
        changeMissionOwner(
            missionId: $missionId
            clientUserId: $clientUserId
        ) {
            ...CoreMissionFields
        }
    }
`);

gql(`
    fragment AuditLogFields on AuditLog {
        id
        text
        createdAt
        userAvatarUrl
        userDisplayName
        event
    }
`);

export const FIND_MISSION_AUDIT_LOGS = gql(`
    query FindMissionAuditLogs($missionId: ID!, $offset: Int, $limit: Int) {
        findMissionAuditLogs(missionId: $missionId, offset: $offset, limit: $limit) {
            elements {
                ...AuditLogFields
            }
            pageInfo {
                ...PageInfoFields
            }
        }
    }
`);

export const GET_CONTRACT = gql(`
    query getContract($id: ID!) {
        getContract(id: $id) {
            ...CoreContractFields
            missionInfo {
                id
                workerInfo {
                    ...WorkerInfoFields
                }
                clientInfo {
                    ...ClientInfoFields
                }
            }
        }
    }
`);

export const GET_TERMINATION_INFO = gql(`
    query getTerminationInfo($missionId: ID!) {
        getTerminationInfo(missionId: $missionId) {
            newState
            minimalEndDate
            cancellationRequest {
                ...MissionCancellationRequestFields
            }
        }
    }
`);

export const DELETE_MISSION = gql(`
    mutation DeleteMission($missionId: ID!) {
        deleteMission(missionId: $missionId)
    }
`);

export const TERMINATE_MISSION = gql(`
    mutation TerminateMission($missionId: ID!, $data: TerminationDataInput!) {
        terminateMission(missionId: $missionId, data: $data) {
            ...CoreMissionFields
        }
    }
`);

export const FIND_ACTIVITY_TYPE = gql(`
    query FindActivityType($query: String!, $offset: Int, $limit: Int) {
        findActivityType(query: $query, offset: $offset, limit: $limit) {
            elements {
                id
                title
            }
            pageInfo {
                ...PageInfoFields
            }
        }
    }
`);

export const GET_PRIVATE_CLIENT_ACTIVITY_TYPES = gql(`
    query GetPrivateClientActivityTypes($stateCode: String, $legalEmployerId: String) {
        getPrivateClientActivityTypes(stateCode: $stateCode, legalEmployerId: $legalEmployerId) {
            key
            name
            activityTypeId
            cctEditionId
        }
    }
`);

export const FIND_CLIENT_INFO = gql(`
    query FindClientInfo($query: String!) {
        findClientInfo(query: $query) {
            elements {
                ...ClientInfoFields
            }
            pageInfo {
                ...PageInfoFields
            }
        }
    }
`);

export const UPDATE_WORKER_PROFILE = gql(`
    mutation updateWorkerProfile($changes: WorkerProfileInput!) {
        updateWorkerProfile(workerProfile: $changes) {
            ...WorkerProfileFields
        }
    }
`);

export const UPDATE_CLIENT_PROFILE = gql(`
    mutation updateClientProfile($changes: ClientProfileInput!) {
        updateClientProfile(clientProfile: $changes) {
            ...ClientProfileFields
        }
    }
`);

export const UPDATE_PRIVATE_CLIENT_PROFILE = gql(`
    mutation updatePrivateClientProfile($changes: PrivateClientProfileInput!) {
        updatePrivateClientProfile(privateClientProfile: $changes) {
            ...ClientProfileFields
        }
    }
`);

export const FIND_WORKER_INFO = gql(`
    query FindWorkerInfo($query: String!) {
        findWorkerInfo(query: $query, limit: 100) {
            count
            elements {
                ...WorkerInfoFields
            }
        }   
    }
`);

export const GET_WORKER_INFO = gql(`
    query GetWorkerInfo($workerId: ID, $phoneNumber: String) {
        getWorkerInfo(workerProfileId: $workerId, phoneNumber: $phoneNumber) {
            ...WorkerInfoFields
        }
    }    
`);

export const GET_CLIENT_INFO = gql(`
    query GetClientInfo($clientProfileId: ID!) {
        getClientInfo(clientProfileId: $clientProfileId) {
            ...ClientInfoFields
        }
    }    
`);

export const REMOVE_WORKER_FROM_CLIENT = gql(`
    mutation RemoveRelationship($workerProfileId: ID!) {
        removeWorkerFromClient(workerProfileId: $workerProfileId)
    }
`);

export const REGENERATE_CONTRACT_ASSETS = gql(`
    mutation RegenerateContractAssets2($contractId: ID!) {
        regenerateContractAssets2(contractId: $contractId) {
            clientAssetId
            workerAssetId
        }
    }
`);

export const CHANGE_MISSION_SIGNER = gql(`
    mutation ChangeMissionSigner(
        $missionId: ID!
        $clientUserId: ID!
    ) {
        changeMissionSigner(
            missionId: $missionId
            clientUserId: $clientUserId
        ) {
            ...CoreMissionFields
        }
    }
`);

export const GENERATE_MISSION_SIMULATION_EMAIL = gql(`
    mutation GenerateAndSendMissionSimulationPdf($missionData: MissionInput!, $workerData: WorkerInfoInput!, $email: String!, $includePdfDetails: Boolean!, $includeContractExamples: Boolean!) {
        generateAndSendMissionSimulationPdf(missionData: $missionData, workerData: $workerData, email: $email, includePdfDetails: $includePdfDetails, includeContractExamples: $includeContractExamples)
    }
`);

export const GET_BANK_DETAILS_FOR_IBAN = gql(`
    query GetBankDetailsForIban($iban: String!) {
        getBankDetailsForIban(iban: $iban) {
            bic
            bankName
        }
    }
`);
